import React from "react";
import { useMediaQuery, Box, TablePagination, Typography, Pagination } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import "@mui/material/colors";
import "@mui/material/locale";
import "@mui/material/styles";
import { useCustomTheme } from "../../style/hooks.js";
const useStyledPaginationClasses = makeStyles()((theme) => ({
  tablePagination: {
    minHeight: 60,
    overflow: "hidden",
    marginTop: theme.spacing(1),
    marginRight: "auto",
    "& .MuiTablePagination-displayedRows": {
      display: "none"
    },
    "& .MuiTablePagination-select": {
      color: theme.palette.secondary.main,
      [theme.breakpoints.down("sm")]: {
        paddingLeft: 0
      }
    },
    "& .MuiTablePagination-selectLabel": {
      color: theme.palette.secondary.main
    },
    "& .MuiTablePagination-caption:first-of-type": {
      color: theme.palette.secondary.main
    },
    "& .MuiTablePagination-caption:last-of-type": {
      display: "none",
      marginRight: theme.spacing(4),
      color: theme.palette.secondary.main
    },
    "& .MuiTablePagination-actions": {
      display: "none",
      marginRight: theme.spacing(4)
    },
    "& .MuiTablePagination-selectIcon": {
      right: theme.spacing(-1),
      color: theme.palette.secondary.main
    },
    "& .MuiTablePagination-select.Mui-focused": {
      "& .MuiTablePagination-select": {
        color: theme.palette.primary.main
      },
      border: "0 !important",
      boxShadow: "none"
    },
    "& .MuiTablePagination-selectRoot": {
      marginLeft: theme.spacing(0.5)
    }
  },
  tablePaginationWithActions: {
    minHeight: 60,
    overflow: "hidden",
    marginTop: theme.spacing(1),
    marginRight: "auto",
    width: "100%",
    "& .MuiTablePagination-displayedRows": {
      display: "none"
    },
    "& .MuiIconButton-root": {
      backgroundColor: theme.palette.background.paper,
      height: "26px",
      width: "26px",
      borderRadius: theme.spacing(0.5),
      border: "1px solid rgb(225 231 235)",
      margin: theme.spacing(1, 0.5)
    },
    "& .MuiIconButton-root:hover": {
      color: theme.palette.background.paper,
      backgroundColor: theme.palette.primary.main
    },
    "& .MuiIconButton-root .MuiSvgIcon-root path": {
      stroke: theme.palette.background.paper,
      strokeWidth: "0.75px"
    },
    "& .MuiIconButton-root:hover .MuiSvgIcon-root path": {
      stroke: theme.palette.primary.main,
      strokeWidth: "0.75px"
    },
    "& .MuiTablePagination-actions": {
      marginRight: theme.spacing(4)
    },
    "& .MuiTablePagination-select": {
      color: theme.palette.secondary.main,
      paddingRight: theme.spacing(1)
    },
    "& .MuiTablePagination-selectLabel": {
      color: theme.palette.secondary.main
    },
    "& .MuiTablePagination-caption:first-of-type": {
      color: theme.palette.secondary.main
    },
    "& .MuiTablePagination-caption:last-of-type": {
      marginRight: theme.spacing(0.75),
      color: theme.palette.secondary.main
    },
    "& .MuiTablePagination-selectIcon": {
      color: theme.palette.secondary.main
    },
    "& .MuiTablePagination-select.Mui-focused .MuiTablePagination-select": {
      color: theme.palette.primary.main
    }
  },
  paginationCaption: {
    alignItems: "center",
    marginRight: theme.spacing(4)
  },
  pagination: {
    display: "flex",
    marginRight: theme.spacing(3),
    "& .MuiPaginationItem-root": {
      height: "26px",
      minWidth: "26px",
      border: "none",
      margin: 0,
      padding: 0,
      color: theme.palette.secondary.main
    },
    "& .MuiPaginationItem-root:hover": {
      color: theme.palette.primary.main,
      backgroundColor: "transparent"
    },
    "& .MuiPaginationItem-root.Mui-selected": {
      backgroundColor: "transparent",
      color: theme.palette.text.primary,
      "&:hover": {
        backgroundColor: "transparent"
      }
    },
    "& .MuiPagination-ul li:first-of-type .MuiPaginationItem-root, & .MuiPagination-ul li:last-of-type .MuiPaginationItem-root": {
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.spacing(0.5),
      border: "1px solid rgb(225 231 235)",
      margin: theme.spacing(0, 0.5)
    },
    "& .MuiPagination-ul li:first-of-type .MuiPaginationItem-root:hover, & .MuiPagination-ul li:last-of-type .MuiPaginationItem-root:hover": {
      color: theme.palette.background.paper,
      backgroundColor: theme.palette.primary.main
    },
    [theme.breakpoints.down("sm")]: {
      marginRight: theme.spacing(2),
      minWidth: 220,
      justifyContent: "end"
    }
  }
}));
const StyledPagination = ({
  count,
  rowsPerPage,
  rowsPerPageOptions,
  page,
  labelRowsPerPage,
  onPageChange,
  onPageSizeChange,
  showPaginationNumbers = true,
  isTotalCountUnknown = false
}) => {
  const { classes } = useStyledPaginationClasses();
  const theme = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const itemCount = Math.ceil(count / rowsPerPage);
  const pageNumber = page + 1;
  const paginateStart = pageNumber * rowsPerPage + 1 - rowsPerPage;
  const emptyCheckedPaginateStart = count === 0 ? 0 : paginateStart;
  const paginateEnd = paginateStart + rowsPerPage - 1;
  const shouldShowPaginationNumbers = showPaginationNumbers && !isTotalCountUnknown;
  const tablePaginationClass = shouldShowPaginationNumbers ? classes.tablePagination : classes.tablePaginationWithActions;
  const tablePaginationCount = isTotalCountUnknown ? -1 : count;
  const isNextButtonDisabled = isTotalCountUnknown && count < rowsPerPage;
  const handlePaginationNumbersPageChange = (_, page2) => {
    if (onPageChange) {
      onPageChange(page2 - 1);
    }
  };
  const handleTablePageChange = (_, page2) => {
    if (onPageChange) {
      onPageChange(page2);
    }
  };
  const handlePageSizeChange = (event) => {
    if (onPageSizeChange) {
      const newPageSize = parseInt(event.target.value);
      if (rowsPerPage !== newPageSize) {
        onPageSizeChange(newPageSize);
      }
    }
  };
  return /* @__PURE__ */ React.createElement(Box, { display: "flex" }, /* @__PURE__ */ React.createElement(
    TablePagination,
    {
      className: tablePaginationClass,
      component: "div",
      count: tablePaginationCount,
      page,
      labelRowsPerPage,
      rowsPerPage,
      rowsPerPageOptions,
      nextIconButtonProps: {
        disableFocusRipple: true,
        disableRipple: true,
        disabled: isNextButtonDisabled
      },
      onPageChange: handleTablePageChange,
      onRowsPerPageChange: handlePageSizeChange
    }
  ), shouldShowPaginationNumbers && /* @__PURE__ */ React.createElement(Box, { display: "flex" }, /* @__PURE__ */ React.createElement(
    Box,
    {
      sx: {
        display: {
          xs: "none",
          sm: "flex"
        }
      },
      className: classes.paginationCaption
    },
    /* @__PURE__ */ React.createElement(Typography, { variant: "body2", color: "textSecondary" }, emptyCheckedPaginateStart, "-", paginateEnd > count ? count : paginateEnd, " of ", count)
  ), /* @__PURE__ */ React.createElement(
    Pagination,
    {
      className: classes.pagination,
      count: itemCount,
      variant: "outlined",
      page: pageNumber,
      shape: "rounded",
      onChange: handlePaginationNumbersPageChange,
      siblingCount: isMobile ? 0 : 1
    }
  )));
};
export {
  StyledPagination as default,
  useStyledPaginationClasses
};
//# sourceMappingURL=StyledPagination.js.map
